import { type ReactNode, createContext, useContext, forwardRef } from 'react';
import type React from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { XIcon } from '@phosphor-icons/react';
import {
  simpleModalTheme,
  type SimpleModalVariantProps,
  type SimpleModalSlots
} from './simple-modal-theme';

type SlotsToClasses = Partial<Record<SimpleModalSlots, string>>;

type ScrollBehavior = 'inside' | 'outside';
const ModalScrollContext = createContext<ScrollBehavior>('outside');

// Turn caller height classes (e.g. `h-full`, `h-[780px]`) into their min-height
// equivalents so the modal always fills the requested height — even with sparse
// content — while `h-auto` lets it still grow taller and scroll in the wrapper.
const toMinHeight = (classes: string): string =>
  classes
    .split(/\s+/)
    .filter((c) => /^!?h-/.test(c))
    .map((c) => c.replace(/^(!?)h-/, '$1min-h-'))
    .join(' ');

// Pull the CSS length out of a caller height class so inside-scroll modals can
// turn it into a scale-compensated, cap-clamped inline `min-height` (a min-height
// *class* can't be scale-compensated and would override `max-height` in CSS).
// `h-[90dvh]` → `90dvh`, `h-full` → `100%`, `h-screen` → `100vh`. Spacing-scale
// heights (`h-150`) return null — they aren't a "fill the viewport" intent.
const extractHeightValue = (classes: string): string | null => {
  const token = classes.split(/\s+/).find((c) => /^!?h-/.test(c));
  if (!token) return null;
  const bare = token.replace(/^!?h-/, '');
  if (bare === 'full') return '100%';
  if (bare === 'screen') return '100vh';
  const arbitrary = bare.match(/^\[(.+)\]$/);
  return arbitrary ? arbitrary[1].replace(/_/g, ' ') : null;
};

interface ModalProps extends Omit<SimpleModalVariantProps, 'isDarkMode'> {
    isOpen: boolean;
    onClose?: () => void;
    onOpenChange?: (open: boolean) => void;
    hideCloseButton?: boolean;
    className?: string;
    classNames?: SlotsToClasses;
    theme?: string;
    isDismissable?: boolean;
    /** Visual scale factor, used to match a scaled presentation stage in embeds. */
    scale?: number;
    /** Tailwind width class (e.g. `w-250`); overrides the `size` variant width. */
    width?: string;
    /** Tailwind height class (e.g. `h-[300px]`); overrides the `size` variant height. */
    height?: string;
    /** Controls where overflow scrolling occurs. 'outside' (default) scrolls the wrapper; 'inside' scrolls the body with header/footer pinned. */
    scrollBehavior?: ScrollBehavior;
    children: ReactNode;
}

export const Modal = ({
  isOpen,
  onClose,
  onOpenChange,
  hideCloseButton = false,
  size = 'md',
  variant = 'default',
  placement = 'top',
  radius = 'lg',
  shadow = 'lg',
  backdrop = 'opaque',
  disableAnimation = false,
  className = '',
  classNames = {},
  theme = '',
  isDismissable = true,
  scale = 1,
  width = '',
  height = '',
  scrollBehavior = 'outside',
  children
}: ModalProps) => {
  // Detect dark mode from theme prop or system
  const isDarkMode = theme.includes('dark') ||
        (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Generate theme classes
  const themeClasses = simpleModalTheme({
    variant,
    placement,
    size,
    radius,
    shadow,
    backdrop,
    isDarkMode,
    disableAnimation,
    scrollBehavior,
  });

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && isDismissable) {
        onClose?.();
        onOpenChange?.(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, onOpenChange, isDismissable]);

  // Render into document.body so the modal escapes any ancestor `transform`
  // (e.g. the PowerPoint edit-mode stage is scaled to fit the slide); a
  // transformed ancestor becomes the containing block for fixed-position
  // descendants and would otherwise shrink the modal with it.
  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && isDismissable) {
      onClose?.();
      onOpenChange?.(false);
    }
  };

  // Inside scroll bounds the box entirely via inline styles (see the comments on
  // `maxHeight`/`minHeight` below). The stage `transform: scale()` shrinks the
  // box, so both bounds are divided by the scale to land at the intended
  // *rendered* size; `min()` keeps the min-height from ever exceeding the cap.
  // `scale` already carries a legibility floor (see usePresentationScale), so it
  // is applied as-is here.
  const isInsideScroll = scrollBehavior === 'inside' && size !== 'full';
  const insideMaxHeight = `calc((100dvh - 3rem) / ${scale})`;
  const callerHeight = extractHeightValue(
    [classNames.base, width, height, className].filter(Boolean).join(' ')
  );

  return createPortal(
    <ModalScrollContext.Provider value={scrollBehavior}>
      <div
        className={`${theme} ${themeClasses.wrapper()} ${classNames.wrapper || ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: size === 'full' ? 50 : 1000,
          pointerEvents: 'auto'
        }}
      >
        {/* Backdrop */}
        <div
          className={`${themeClasses.backdrop()} ${classNames.backdrop || ''}`}
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: size === 'full' ? 0 : -1
          }}
        />

        {/* Modal Content */}
        <div
          className={twMerge(
            themeClasses.base(),
            classNames.base,
            width,
            height,
            className,
            // `h-auto` lets the box grow with content; it also overrides any
            // caller `h-full`/`h-[…]` so inside scroll is bounded purely by the
            // scale-compensated `maxHeight` set below (a `min-height` from the
            // caller would override `max-height` in CSS and overflow the
            // viewport). Outside scroll instead turns the caller height into a
            // *minimum* and drops the cap, so the whole modal scrolls in the
            // wrapper and still fills the requested height when content is sparse.
            scrollBehavior === 'outside' && toMinHeight([classNames.base, width, height, className].filter(Boolean).join(' ')),
            'h-auto',
            scrollBehavior === 'outside' && 'max-h-none'
          )}
          style={{
            zIndex: size === 'full' ? 0 : 1,
            position: 'relative',
            // Inside scroll caps the box so the body (not the wrapper) scrolls.
            maxHeight: isInsideScroll ? insideMaxHeight : undefined,
            // A caller height (e.g. `h-[90dvh]`) becomes a *minimum* so the modal
            // holds that height even with no content, while `h-auto` + the cap
            // above let it grow with content and then scroll the body. `min()`
            // clamps it to the cap so a tall request (or `h-full`) can't override
            // `max-height` and overflow the viewport.
            minHeight: isInsideScroll && callerHeight
              ? `min(${insideMaxHeight}, calc(${callerHeight} / ${scale}))`
              : undefined,
            // Always establish a containing block (transform) so any
            // `position: fixed` descendant — e.g. a sticky sidebar — is anchored
            // to the modal, matching how HeroUI's transformed content behaves.
            transform: scale !== 1 && size !== 'full'
              ? `scale(${scale}) translateZ(0)`
              : 'translateZ(0)',
            transformOrigin: placement.startsWith('top')
              ? 'center top'
              : placement.startsWith('bottom')
                ? 'center bottom'
                : 'center'
          }}
        >
          {/* Close Button */}
          {!hideCloseButton && (
            <button
              onClick={() => {
                onClose?.();
                onOpenChange?.(false);
              }}
              className={`${themeClasses.closeButton()} ${classNames.closeButton || ''}`}
              style={{ zIndex: 10 }}
              aria-label="Close modal"
            >
              <XIcon size={24} />
            </button>
          )}

          {children}
        </div>
      </div>
    </ModalScrollContext.Provider>,
    document.body
  );
};

interface ModalContentProps {
    className?: string;
    children: ReactNode;
}

export const ModalContent = ({ className = '', children }: ModalContentProps) => {
  return (
    <div className={twMerge('flex flex-col w-full flex-1 min-h-0', className)}>
      {children}
    </div>
  );
};

interface ModalBodyProps extends Omit<SimpleModalVariantProps, 'isDarkMode'> {
    className?: string;
    children: ReactNode;
}

export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(({
  className = '',
  variant = 'default',
  size = 'md',
  radius = 'lg',
  shadow = 'lg',
  backdrop = 'opaque',
  disableAnimation = false,
  children
}, ref) => {
  const scrollBehavior = useContext(ModalScrollContext);
  const themeClasses = simpleModalTheme({
    variant,
    size,
    radius,
    shadow,
    backdrop,
    disableAnimation,
    scrollBehavior,
  });

  return (
    <div ref={ref} className={`${themeClasses.body()} flex-1 min-h-0 ${className}`}>
      {children}
    </div>
  );
});

ModalBody.displayName = 'ModalBody';

interface ModalHeaderProps extends Omit<SimpleModalVariantProps, 'isDarkMode'> {
    className?: string;
    children: ReactNode;
}

export const ModalHeader = ({
  className = '',
  variant = 'default',
  size = 'md',
  radius = 'lg',
  shadow = 'lg',
  backdrop = 'opaque',
  disableAnimation = false,
  children
}: ModalHeaderProps) => {
  const themeClasses = simpleModalTheme({
    variant,
    size,
    radius,
    shadow,
    backdrop,
    disableAnimation,
  });

  return (
    <div className={`${themeClasses.header()} ${className}`}>
      {children}
    </div>
  );
};

interface ModalFooterProps extends Omit<SimpleModalVariantProps, 'isDarkMode'> {
    className?: string;
    children: ReactNode;
}

export const ModalFooter = ({
  className = '',
  variant = 'default',
  size = 'md',
  radius = 'lg',
  shadow = 'lg',
  backdrop = 'opaque',
  disableAnimation = false,
  children
}: ModalFooterProps) => {
  const themeClasses = simpleModalTheme({
    variant,
    size,
    radius,
    shadow,
    backdrop,
    disableAnimation,
  });

  return (
    <footer className={`${themeClasses.footer()} ${className}`}>
      {children}
    </footer>
  );
};
